import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';

import { ApiService } from '@app/core/services/api/api.service';
import { Carrier, CarrierTypes } from '@app/services/carriers/carrier.model';
import { Shop } from '@app/services/shops/shop.model';
import { QueryResponse, QueryResponseStatus } from '@app/sharedModule/models/query-response.model';
import { getApiUrl } from '@app/core/services/utils/util.service';
import { CarrierTypeNames } from '@pdqdev/pdq-shared/dist/CarrierData';
import { ShopService } from '@app/services/shops/shop.service';

export const FEDEX_VERIFICATION_STATUS_FIELD = 'fedexVerificationStatus';
export const FEDEX_VERIFICATION_OPTION_FIELD = 'fedexVerificationOption';
export const FEDEX_VERIFICATION_PENDING = 'PENDING';
export const FEDEX_DEFAULT_VERIFICATION_METHOD = 'SMS';

interface CarrierMutationResponse extends QueryResponse {
  requiresVerification?: boolean;
}

export type FedExVerificationData = {
  verificationType: 'PIN';
  pin: string;
};

export interface FedExVerificationResponse {
  status?: string;
  message?: string;
}

@Injectable()
export class CarriersService {
  API_URL = getApiUrl();

  constructor(
    private readonly apiService: ApiService,
    private readonly http: HttpClient,
    private readonly shopService: ShopService,
  ) {}

  loadShopsUser(): Observable<Shop[]> {
    return this.apiService.get('me').pipe(
      map((response) => {
        const shops: Shop[] = response.shops;
        return shops;
      }),
    );
  }

  loadAllCarrierTypes(): Observable<CarrierTypes[]> {
    return this.apiService.get(`carriers/list`).pipe(
      map((response: QueryResponse) => {
        if (response.status === QueryResponseStatus.FAILED) {
          throw response.message;
        }
        return response.data;
      }),
    );
  }

  loadCarriersByShop(): Observable<Carrier[]> {
    return this.apiService.get(`carriers/list/instances`).pipe(
      map((response: QueryResponse) => {
        if (response.status === QueryResponseStatus.FAILED) {
          throw response.message;
        }
        return response.data;
      }),
    );
  }

  loadCarrierByID(carrierID: number, shopID: number): Observable<Carrier> {
    return this.apiService
      .get('carriers/carrier', {
        params: {
          carrierId: carrierID.toString(),
          shopId: shopID.toString(),
        },
      })
      .pipe(
        map((response: QueryResponse) => {
          if (response.status === QueryResponseStatus.FAILED) {
            throw response.message;
          }

          return response.data;
        }),
      );
  }

  addNewCarrier(carrier: Carrier, shopId: number): Observable<{ carrier: Carrier; requiresVerification?: boolean }> {
    return this.apiService
      .post('carriers/create-carrier', {
        carrier,
        shopId,
      })
      .pipe(
        map((response: CarrierMutationResponse) => {
          if (response.status === QueryResponseStatus.FAILED) {
            throw response.message;
          }

          return {
            carrier: response.data,
            requiresVerification: response.requiresVerification || false,
          };
        }),
      );
  }

  editCarrier(carrier: Carrier, shopId: number): Observable<{ carrier: Carrier; requiresVerification?: boolean }> {
    return this.apiService
      .post('carriers/edit-carrier', {
        carrier,
        shopId,
      })
      .pipe(
        share(),
        map((response: CarrierMutationResponse) => {
          if (response.status === QueryResponseStatus.FAILED) {
            throw response.message;
          }
          return {
            carrier: response.data,
            requiresVerification: response.requiresVerification || false,
          };
        }),
      );
  }

  deleteCarrier(carrierId: number, shopId: number): Observable<QueryResponse> {
    return this.apiService
      .delete('carriers/carrier', {
        params: {
          carrierId: carrierId.toString(),
          shopId: shopId.toString(),
        },
      })
      .pipe(
        map((response: QueryResponse) => {
          if (response.status === QueryResponseStatus.FAILED) {
            throw response.message;
          }
          return response;
        }),
      );
  }

  // getMetaDataConfig(carrierType: CarrierTypes, carrierId: number) {
  //   return this.apiService
  //     .get('carriers/carrier/custom-fields', {
  //       params: {
  //         carrierType: carrierType.toString(),
  //         carrierId: carrierId?.toString(),
  //       },
  //     })
  //     .pipe(
  //       map((response: QueryResponse) => {
  //         if (
  //           response.status === QueryResponseStatus.FAILED &&
  //           !Object.keys(response.data).length
  //         ) {
  //           throw response.message;
  //         }

  //         return response.data;
  //       }),
  //     );
  // }

  getCarrierFieldsConfig(carrierType: CarrierTypeNames, carrierId?: number) {
    const params: any = {
      carrierType: carrierType,
    };
    if (carrierId) {
      params.carrierId = carrierId;
    }
    return this.apiService
      .get('carriers/carrier/carrier-fields', {
        params,
      })
      .pipe(
        map((response: QueryResponse) => {
          if (response.status === QueryResponseStatus.FAILED && !Object.keys(response.data).length) {
            throw response.message;
          }

          return response.data;
        }),
      );
  }

  getCarrierCustomFieldValue(carrierTypeName: CarrierTypeNames, fieldName: string) {
    return this.apiService
      .get('carriers/carrier-custom-field', {
        params: {
          carrierTypeName,
          fieldName,
        },
      })
      .pipe(
        map((response: QueryResponse) => {
          if (response.status === QueryResponseStatus.FAILED && !Object.keys(response.data).length) {
            throw response.message;
          }

          return response.data;
        }),
      );
  }

  isCarrierRequiresExternalAuthentication(carrier?: Carrier): boolean {
    if (!this.shopService.metadata?.enableFedexVerification) {
      return false;
    }

    const externalAuthenticationConfig = carrier?.metadata?.find(
      (field) => field?.fieldName === 'isExternallyAuthenticated',
    );
    const isRequiresExtneralAuthentication =
      externalAuthenticationConfig &&
      ((externalAuthenticationConfig as any).defaultValue === false || externalAuthenticationConfig.default === false);
    return isRequiresExtneralAuthentication;
  }

  startCarrierExternalAuthentication(carrierId: number, shopId: number) {
    return this.apiService
      .post('carriers/external-authentication/start', {
        carrierId,
      })
      .subscribe(
        (response) => {
          if (response?.redirectUrl) {
            window.location.href = response?.redirectUrl;
          } else {
            console.log('Redirect URL is not provided');
          }
        },
        (err) => {
          console.log('Error: ', err);
        },
      );
  }

  isCarrierRequiresVerification(carrier?: Carrier): boolean {
    const verificationStatusField = carrier?.metadata?.find(
      (field) => field?.fieldName === FEDEX_VERIFICATION_STATUS_FIELD,
    );
    return verificationStatusField?.default === FEDEX_VERIFICATION_PENDING;
  }

  completeCarrierVerification(
    carrierId: number,
    verificationData: FedExVerificationData,
  ): Observable<FedExVerificationResponse> {
    return this.apiService.post('carriers/carrier/complete-verification', {
      carrierId,
      verificationData,
    });
  }

  resendCarrierVerification(carrierId: number): Observable<FedExVerificationResponse> {
    return this.apiService.post('carriers/carrier/resend-verification', {
      carrierId,
    });
  }
}
